import { useQuery } from "@tanstack/react-query";

import { patientsApi } from "../apis";
import { Patient } from "../types/patient";

export interface UsePatientDetailForProviderResult {
  patient: Patient | null;
  hasRequestedSuccessfullyBefore: boolean;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const usePatientDetailForProvider = (
  patientId: string
): UsePatientDetailForProviderResult => {
  const {
    data,
    isLoading: loading,
    error: queryError,
    refetch,
  } = useQuery({
    queryKey: ["patientForProvider", patientId],
    queryFn: async () => {
      if (!patientId) return null;

      const result = await patientsApi.getPatientByPatientIdForProvider({
        patientId,
      });

      return result;
    },
    enabled: !!patientId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2, // Retry failed requests 2 times
    retryDelay: 1000, // Wait 1 second between retries
    refetchOnWindowFocus: false, // Don't refetch when window regains focus
  });

  // Transform query error to string
  const error = queryError
    ? queryError instanceof Error
      ? queryError.message
      : "Unknown error"
    : null;

  return {
    patient: data?.patient as Patient | null,
    hasRequestedSuccessfullyBefore:
      data?.hasRequestedSuccessfullyBefore ?? false,
    loading,
    error,
    refetch,
  };
};

export default usePatientDetailForProvider;
